import { CoachSettings, PricePlanTier } from "../../../../interfaces";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import VideoPageCTA from "./VideoPageCTA";
import {
  useSocialCoachSession,
  usePlayerSettings,
  URLS,
  blogDetailUrl,
  useExistingBlog,
  useHasBlogAccess,
} from "../../../../utils";
import {
  ContentPage,
  ContentPageServices,
  ContentPageThemeServices,
  CTA,
  Media,
  normalizeCTA,
  UpdateContentPageRequest,
} from "@/services/bedrock";
import { SuperPageIcon } from "@/containers/SuperPages";
import EditVideoPageVideoSection from "./EditVideoPageVideoSection";
import { Badge, Button, Card, Combobox, confirmDialog, DropdownMenu, Field, Input } from "@/components/ui";
import { ChevronDown, Settings } from "lucide-react";
import { LockableFieldLabel } from "./LockableFieldLabel";
import { useHistory } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";

interface OwnProps {
  videoPage: ContentPage;
  readOnly?: boolean;
  onUpdate: (page: ContentPage) => void;
  onDeleted: (page: ContentPage) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

type Props = OwnProps;

type ThemeOption = { id: number; label: string };

type EditVideoPageFormValues = {
  title: string;
  callToAction: CTA | null;
  contentPageThemeId: number | null;
};

const EditVideoPageCard: React.FC<Props> = ({ videoPage, readOnly = false, onUpdate, onDeleted, onDirtyChange }) => {
  const [saving, setSaving] = useState(false);
  const { hasSetting } = usePlayerSettings(videoPage.playerId);
  const { isPlayer, playerId, session } = useSocialCoachSession();
  const history = useHistory();
  const hasSCPlus = isPlayer && session?.subscription?.tier === PricePlanTier.PLAYER_PLUS;
  const hasBlogAccess = useHasBlogAccess(videoPage.playerId);
  const { existingBlog, loadingExistingBlog } = useExistingBlog(
    { contentPageId: videoPage.id },
    hasBlogAccess && !!videoPage.id
  );
  const [themeOptions, setThemeOptions] = useState<ThemeOption[]>([]);

  const hasThemeAccess = useMemo(() => {
    return !isPlayer || hasSetting(CoachSettings.PLAYER_MVP_THEMES);
  }, [hasSetting, isPlayer]);

  const canGenerateBlog = useMemo(() => {
    // Held back until the lookup settles, so a stale menu cannot start a second blog.
    if (loadingExistingBlog || existingBlog) return false;
    return hasBlogAccess && !readOnly && (videoPage.originalMedia?.length ?? 0) > 0;
  }, [loadingExistingBlog, existingBlog, hasBlogAccess, readOnly, videoPage.originalMedia]);
  const { superPage, superPageAssignment } = videoPage;
  const isSuperPageTemplate = superPage?.templateContentPageId === videoPage.id;

  const resolveFormValues = useCallback((): EditVideoPageFormValues => {
    let { title, callToAction, contentPageThemeId } = videoPage;

    if (superPage) {
      if (superPage.allowTitleChange) {
        if (!videoPage.title) title = superPage.templateContentPage?.title ?? "";
      } else {
        title = superPage.templateContentPage?.title ?? "";
      }
      if (superPage.allowCtaChange) {
        if (!videoPage.callToAction) callToAction = superPage.callToAction;
      } else {
        callToAction = superPage.callToAction;
      }
      if (superPage.allowThemeChange) {
        if (!videoPage.contentPageThemeId) contentPageThemeId = superPage.contentPageThemeId;
      } else {
        contentPageThemeId = superPage.contentPageThemeId;
      }
    }

    return {
      title: title ?? "",
      callToAction: normalizeCTA(callToAction),
      contentPageThemeId: contentPageThemeId ?? null,
    };
  }, [videoPage, superPage]);

  const methods = useForm<EditVideoPageFormValues>({
    defaultValues: resolveFormValues(),
  });
  const {
    register,
    control,
    watch,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    reset(resolveFormValues());
  }, [resolveFormValues, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!hasThemeAccess) return;

    ContentPageThemeServices.getPageThemes()
      .then(themeResponse => {
        setThemeOptions(
          themeResponse.content.map(theme => ({
            id: theme.id,
            label: theme.name,
          }))
        );
      })
      .catch(console.error);
  }, [hasThemeAccess]);

  const contentPageThemeId = watch("contentPageThemeId");

  const themeItems = useMemo(() => {
    const merged = [...themeOptions];
    if (contentPageThemeId != null && !merged.some(t => t.id === contentPageThemeId)) {
      merged.unshift({ id: contentPageThemeId, label: "Theme" });
    }
    return merged;
  }, [themeOptions, contentPageThemeId]);

  const themeComboboxValue = useMemo((): ThemeOption | null => {
    if (contentPageThemeId == null) return null;
    return themeItems.find(t => t.id === contentPageThemeId) ?? null;
  }, [contentPageThemeId, themeItems]);

  const savePage = useCallback(
    (originalMedia?: Media[]) => {
      handleSubmit(formValues => {
        if (!videoPage) return;

        setSaving(true);
        const request: UpdateContentPageRequest = {
          title: formValues.title,
          callToAction: formValues.callToAction,
          themeId: formValues.contentPageThemeId,
        };
        if (originalMedia) {
          request.originalMedia = originalMedia;
          request.videoProjectId = null;
        }
        ContentPageServices.updateContentPage(videoPage.id, request)
          .then(updatedPage => {
            onUpdate(updatedPage);
            toast.success("Page saved successfully.");
          })
          .catch(error => {
            console.error(error);
            toast.error("Unable to save page.");
          })
          .finally(() => setSaving(false));
      })();
    },
    [videoPage, handleSubmit, onUpdate]
  );

  const archivePage = async () => {
    const confirmed = await confirmDialog({
      title: "Delete Video Page",
      description: "Are you sure you want to delete this Video Page? You cannot undo this action.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (confirmed) {
      await ContentPageServices.archiveContentPage(videoPage.id);
      toast.success("Video Page has been deleted.");
      onDeleted(videoPage);
    }
  };

  const downloadVideo = () => {
    const media = videoPage!.originalMedia[0];
    const filename = `video-${videoPage!.slug}.${media.extension}`;
    fetch(media.uri)
      .then(response => response.blob())
      .then(blob => {
        const urlObject = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlObject;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlObject);
      });
  };

  const viewVideoProject = useCallback(() => {
    if (!videoPage.videoProjectId) return;
    if (isPlayer) {
      history.push(URLS.player.videoProject.replace(":id", String(videoPage.videoProjectId)));
    } else {
      history.push(URLS.coach.videoProject.replace(":id", String(videoPage.videoProjectId)));
    }
  }, [videoPage.videoProjectId, history, isPlayer]);

  const submitVideoForEditing = useCallback(() => {
    const currentVideo = videoPage.originalMedia?.[0];
    if (!currentVideo || !isPlayer) return;

    const media = { ...currentVideo, id: -1 } as Media;
    history.push(URLS.player.newVideoProject, {
      preloadMedia: media,
      contentPageId: videoPage.id,
      scriptId: superPage?.videoScriptId,
    });
  }, [videoPage.id, videoPage.originalMedia, history, superPage?.videoScriptId, isPlayer]);

  return (
    <FormProvider {...methods}>
      <Card>
        <Card.Header className="flex flex-row flex-wrap items-center gap-2 border-b border-border pb-4">
          {superPage && <SuperPageIcon size="sm" />}
          <Card.Title className="min-w-0 flex-1 flex flex-col">
            {superPage?.title || "Page Details"}
            {superPageAssignment &&
              (superPageAssignment.status === "COMPLETED" ? (
                <span className="text-xs text-green-500">Assignment Completed</span>
              ) : (
                <span className="text-xs text-amber-600">Assignment Pending</span>
              ))}
          </Card.Title>
          <div className="flex flex-row gap-2">
            {videoPage.videoProjectId ? (
              <Button variant="outline" onClick={viewVideoProject}>
                View Video Project
              </Button>
            ) : (
              hasSCPlus &&
              !readOnly &&
              !!superPageAssignment &&
              (videoPage.originalMedia?.length ?? 0) > 0 && (
                <Button variant="outline" onClick={submitVideoForEditing}>
                  Submit for Editing
                </Button>
              )
            )}
            <div className="flex flex-row gap-0">
              <Button
                variant="outline"
                className="rounded-r-none border-r-0 border-border"
                onClick={() => savePage()}
                disabled={!isDirty || readOnly}
                loading={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <DropdownMenu>
                <DropdownMenu.Trigger
                  render={props => (
                    <Button {...props} variant="outline" className="rounded-l-none border-border px-2">
                      <ChevronDown className="size-3 text-muted-foreground" />
                    </Button>
                  )}
                />
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onClick={downloadVideo}>Download Video</DropdownMenu.Item>
                  {existingBlog && hasBlogAccess && (
                    <DropdownMenu.Item onClick={() => history.push(blogDetailUrl(!!isPlayer, existingBlog.id))}>
                      View Blog
                    </DropdownMenu.Item>
                  )}
                  {canGenerateBlog && (
                    <DropdownMenu.Item
                      onClick={() => history.push(`${blogDetailUrl(!!isPlayer, "new")}?contentPageId=${videoPage.id}`)}
                    >
                      Generate Blog
                    </DropdownMenu.Item>
                  )}
                  {!readOnly && (
                    <DropdownMenu.Item variant="destructive" onClick={archivePage}>
                      Delete Page
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          </div>
          {superPage?.categories && superPage.categories.length > 0 && (
            <div className="w-full flex flex-wrap gap-1 mt-2">
              {superPage.categories.map(category => (
                <Badge
                  key={`category-${category.id}`}
                  className="pointer-events-none font-normal select-none"
                  size="small"
                  variant="label"
                  title={category.name}
                >
                  {category.name}
                </Badge>
              ))}
            </div>
          )}
        </Card.Header>
        <Card.Content>
          <div className="flex flex-col gap-4 text-base">
            <Field
              name="title"
              label={
                <LockableFieldLabel
                  title="Page Title (optional)"
                  isLocked={!!superPage && !superPage.allowTitleChange}
                />
              }
            >
              <Input
                id="title"
                placeholder="Page Title"
                disabled={readOnly || (!!superPage && !superPage.allowTitleChange)}
                {...register("title")}
              />
            </Field>
            <VideoPageCTA
              playerId={videoPage.playerId ?? undefined}
              disabled={readOnly}
              isLocked={!!superPage && !superPage.allowCtaChange}
            />
            {hasThemeAccess && (
              <Controller
                name="contentPageThemeId"
                control={control}
                render={({ field }) => (
                  <Field
                    name="theme"
                    label={
                      <LockableFieldLabel title="Page Theme" isLocked={!!superPage && !superPage?.allowThemeChange} />
                    }
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Combobox
                        id="theme"
                        className="flex-1 min-w-0 w-full"
                        placeholder="Default Theme"
                        items={themeItems}
                        value={themeComboboxValue}
                        showClear
                        disabled={readOnly || (!!superPage && !superPage.allowThemeChange)}
                        onValueChange={item => field.onChange(item?.id ?? null)}
                        itemToStringLabel={item => item.label}
                      />
                      <Button
                        variant="ghost"
                        size="icon-lg"
                        aria-label="Open MVP theme settings"
                        onClick={() => {
                          const url = playerId ? URLS.player.mvpSettings : URLS.coach.mvpSettings;
                          window.open(url, "_blank");
                        }}
                      >
                        <Settings className="size-5" />
                      </Button>
                    </div>
                  </Field>
                )}
              />
            )}
            {superPage && !isSuperPageTemplate && (
              <EditVideoPageVideoSection videoPage={videoPage} superPage={superPage} onUpdateMedia={savePage} />
            )}
          </div>
        </Card.Content>
      </Card>
    </FormProvider>
  );
};

export default EditVideoPageCard;
